import React, { createContext, useContext, useState, useEffect } from 'react';

import { doc, getDoc } from 'firebase/firestore';

import UserInfo from 'interfaces/UserInfo';
import { db } from 'services/firebase';

import { useUserData } from './auth';

interface UserInfoContextData {
  userInfo: UserInfo | null;
  userInfoLoading: boolean;
}

const UserInfoContext = createContext<UserInfoContextData>(
  {} as UserInfoContextData,
);

const UserInfoProvider: React.FC = ({ children }) => {
  const { user } = useUserData();

  const [userInfoLoading, setUserInfoLoading] = useState(false);

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

  useEffect(() => {
    const getUserInfo = async (): Promise<void> => {
      setUserInfoLoading(true);
      try {
        if (!user) return;

        const userDoc = await getDoc(doc(db, 'userinfo', user.uid));
        setUserInfo(userDoc.data() as UserInfo);
      } catch {
        await getUserInfo();
      } finally {
        setUserInfoLoading(false);
      }
    };
    getUserInfo();
  }, [user]);

  return (
    <UserInfoContext.Provider value={{ userInfo, userInfoLoading }}>
      {children}
    </UserInfoContext.Provider>
  );
};

/** Handles the userInfo context */
function useUserInfo(): UserInfoContextData {
  const context = useContext(UserInfoContext);

  if (!context) {
    throw new Error('useUserInfo must be used within a UserInfoProvider');
  }

  return context;
}

export { UserInfoProvider, useUserInfo };
